import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, ChevronRight, ChevronLeft, Phone, MessageCircle, Award, ShieldCheck, CreditCard, Crown } from 'lucide-react';
import axios from 'axios';
import ProductCard from '../components/ProductCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Premium Banner Slides
const bannerSlides = [
  {
    id: 1,
    title: "WEAR YOUR",
    titleHighlight: "IDENTITY",
    tagline: "PREMIUM STYLE. PREMIUM YOU.",
    image: "https://images.pexels.com/photos/1342609/pexels-photo-1342609.jpeg?auto=compress&cs=tinysrgb&w=1920",
    cta: "Shop Now",
    link: "/products"
  },
  {
    id: 2,
    title: "PREMIUM",
    titleHighlight: "FOOTWEAR",
    tagline: "STEP INTO ELEGANCE.",
    image: "https://images.pexels.com/photos/267320/pexels-photo-267320.jpeg?auto=compress&cs=tinysrgb&w=1920",
    cta: "Shop Now",
    link: "/products"
  },
  {
    id: 3,
    title: "PLUS SIZE",
    titleHighlight: "COLLECTION",
    tagline: "STYLE FOR EVERY SIZE.",
    image: "https://images.pexels.com/photos/1043474/pexels-photo-1043474.jpeg?auto=compress&cs=tinysrgb&w=1920",
    cta: "Explore",
    link: "/products"
  }
];

export const Home = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % bannerSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const loadData = async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/categories`)
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % bannerSlides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + bannerSlides.length) % bannerSlides.length);

  const featuredProducts = products.filter(p => p.is_featured).slice(0, 4);
  const newArrivals = products.filter(p => p.is_new_arrival).slice(0, 4);
  const displayFeatured = featuredProducts.length > 0 ? featuredProducts : products.slice(0, 4);
  const displayNewArrivals = newArrivals.length > 0 ? newArrivals : products.slice(2, 6);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#FAF9F6]">
        <div className="text-center">
          <Crown className="w-10 h-10 text-[#C59746] mx-auto mb-4 animate-pulse" />
          <p className="text-xs tracking-[0.3em] uppercase text-[#1A1A1A]/60">Loading</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#FAF9F6]" data-testid="home-page">
      
      {/* Hero Banner - Reduced Height */}
      <section className="relative h-[60vh] min-h-[400px] max-h-[550px] overflow-hidden -mt-[104px] lg:-mt-[112px] pt-[104px] lg:pt-[112px]" data-testid="hero-carousel">
        {bannerSlides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-all duration-1000 ease-in-out ${
              index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
            }`}
          >
            <div className="absolute inset-0">
              <img 
                src={slide.image}
                alt={slide.title}
                className="w-full h-full object-cover"
                loading={index === 0 ? "eager" : "lazy"}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A1A]/85 via-[#1A1A1A]/50 to-transparent" />
            </div>
            
            <div className={`relative z-10 h-full flex flex-col justify-center px-6 md:px-12 lg:px-20 max-w-[1600px] mx-auto`}>
              <div className={`max-w-xl transition-all duration-700 delay-200 ${
                index === currentSlide ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
              }`}>
                <h1 className="font-heading mb-1">
                  <span className="block text-3xl md:text-4xl lg:text-5xl font-light text-white tracking-wide">
                    {slide.title}
                  </span>
                  <span className="block text-4xl md:text-5xl lg:text-6xl font-light text-[#C59746] tracking-wide">
                    {slide.titleHighlight}
                  </span>
                </h1>
                <p className="text-white/70 text-xs md:text-sm tracking-[0.2em] uppercase mt-4 mb-6">
                  {slide.tagline}
                </p>
                <div className="flex gap-3">
                  <Link 
                    to={slide.link}
                    className="inline-flex items-center gap-2 bg-[#C59746] text-white px-6 md:px-8 py-3 text-xs tracking-[0.15em] uppercase font-medium hover:bg-[#B8863F] transition-all"
                  >
                    {slide.cta}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link 
                    to="/products"
                    className="inline-flex items-center gap-2 bg-transparent border border-white/40 text-white px-6 md:px-8 py-3 text-xs tracking-[0.15em] uppercase font-medium hover:bg-white hover:text-[#1A1A1A] transition-all"
                  >
                    Explore
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Navigation */}
        <button onClick={prevSlide} className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-all">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button onClick={nextSlide} className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-all">
          <ChevronRight className="w-5 h-5" />
        </button>

        {/* Dots */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
          {bannerSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentSlide ? 'bg-[#C59746] w-6' : 'bg-white/50'
              }`}
            />
          ))}
        </div>
      </section>

      {/* Feature Strip */}
      <section className="bg-[#1A1A1A] py-4">
        <div className="max-w-[1600px] mx-auto px-4">
          <div className="grid grid-cols-3 gap-2 md:gap-4">
            <div className="flex items-center justify-center gap-2 py-1">
              <Award className="w-5 h-5 md:w-6 md:h-6 text-[#C59746]" strokeWidth={1.5} />
              <span className="text-[10px] md:text-xs tracking-wider uppercase text-white font-medium">Premium Quality</span>
            </div>
            <div className="flex items-center justify-center gap-2 py-1 border-x border-white/10">
              <CreditCard className="w-5 h-5 md:w-6 md:h-6 text-[#C59746]" strokeWidth={1.5} />
              <span className="text-[10px] md:text-xs tracking-wider uppercase text-white font-medium">Best Prices</span>
            </div>
            <div className="flex items-center justify-center gap-2 py-1">
              <ShieldCheck className="w-5 h-5 md:w-6 md:h-6 text-[#C59746]" strokeWidth={1.5} />
              <span className="text-[10px] md:text-xs tracking-wider uppercase text-white font-medium">Secure Payment</span>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products - Immediately Visible */}
      <section className="py-12 md:py-16" data-testid="featured-products">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8">
          <div className="flex items-end justify-between mb-8">
            <div>
              <span className="text-[#C59746] text-xs tracking-[0.2em] uppercase mb-1 block">Handpicked</span>
              <h2 className="font-heading text-2xl md:text-3xl font-light text-[#1A1A1A]">
                Featured Products
              </h2>
            </div>
            <Link to="/products" className="text-xs tracking-wider uppercase text-[#1A1A1A] hover:text-[#C59746] transition-colors flex items-center gap-1">
              View All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
            {displayFeatured.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>

      {/* Shop by Category */}
      <section className="py-12 md:py-16 bg-white" data-testid="categories-section">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <span className="text-[#C59746] text-xs tracking-[0.2em] uppercase mb-1 block">Browse</span>
            <h2 className="font-heading text-2xl md:text-3xl font-light text-[#1A1A1A]">
              Shop By Category
            </h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
            {[
              { name: "Clothing", image: "https://images.pexels.com/photos/297933/pexels-photo-297933.jpeg?auto=compress&cs=tinysrgb&w=400" },
              { name: "Footwear", image: "https://images.pexels.com/photos/293405/pexels-photo-293405.jpeg?auto=compress&cs=tinysrgb&w=400" },
              { name: "Plus Size", image: "https://images.pexels.com/photos/1043474/pexels-photo-1043474.jpeg?auto=compress&cs=tinysrgb&w=400" },
              { name: "T-Shirts", image: "https://images.pexels.com/photos/1656684/pexels-photo-1656684.jpeg?auto=compress&cs=tinysrgb&w=400" },
              { name: "Accessories", image: "https://images.pexels.com/photos/1152077/pexels-photo-1152077.jpeg?auto=compress&cs=tinysrgb&w=400" }
            ].map((cat, index) => (
              <Link key={index} to="/products" className="group relative aspect-[3/4] overflow-hidden">
                <img src={cat.image} alt={cat.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/70 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4 text-center">
                  <h3 className="text-white font-heading text-base md:text-lg">{cat.name}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* New Arrivals */}
      <section className="py-12 md:py-16" data-testid="new-arrivals-section">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8">
          <div className="flex items-end justify-between mb-8">
            <div>
              <span className="text-[#C59746] text-xs tracking-[0.2em] uppercase mb-1 block">Just In</span>
              <h2 className="font-heading text-2xl md:text-3xl font-light text-[#1A1A1A]">
                New Arrivals
              </h2>
            </div>
            <Link to="/products" className="text-xs tracking-wider uppercase text-[#1A1A1A] hover:text-[#C59746] transition-colors flex items-center gap-1">
              View All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
            {displayNewArrivals.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>

      {/* Store Locations */}
      <section className="py-12 md:py-16 bg-white" data-testid="stores-section">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <span className="text-[#C59746] text-xs tracking-[0.2em] uppercase mb-1 block">Visit Us</span>
            <h2 className="font-heading text-2xl md:text-3xl font-light text-[#1A1A1A]">Our Stores</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Bhuj */}
            <div className="bg-[#FAF9F6] p-6 border border-[#1A1A1A]/10">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <span className="text-[#C59746] text-[10px] tracking-wider uppercase">Flagship</span>
                  <h3 className="font-heading text-xl text-[#1A1A1A]">Bhuj</h3>
                </div>
                <a href="https://share.google/32ArVO6ALVxQlloIC" target="_blank" rel="noopener noreferrer" className="bg-[#1A1A1A] text-white px-3 py-1.5 text-[10px] tracking-wider uppercase flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> Map
                </a>
              </div>
              <p className="text-sm text-[#1A1A1A]/70 mb-3">Ground Floor, Alfa Arcade, Shop No. 005, Sumra Deli Road, Sonivad, Bhuj - 370001</p>
              <a href="tel:+919328566392" className="text-sm text-[#1A1A1A] hover:text-[#C59746] flex items-center gap-2">
                <Phone className="w-4 h-4 text-[#C59746]" /> +91 93285 66392
              </a>
            </div>
            
            {/* Anjar */}
            <div className="bg-[#FAF9F6] p-6 border border-[#1A1A1A]/10">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <span className="text-[#C59746] text-[10px] tracking-wider uppercase">Branch</span>
                  <h3 className="font-heading text-xl text-[#1A1A1A]">Anjar</h3>
                </div>
                <a href="https://share.google/2mS7TkcySxIfLGGS6" target="_blank" rel="noopener noreferrer" className="bg-[#1A1A1A] text-white px-3 py-1.5 text-[10px] tracking-wider uppercase flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> Map
                </a>
              </div>
              <p className="text-sm text-[#1A1A1A]/70 mb-3">Near Guddi Hotel, Devaliya Naka, Anjar, Gujarat - 370110</p>
              <a href="tel:+918469082367" className="text-sm text-[#1A1A1A] hover:text-[#C59746] flex items-center gap-2">
                <Phone className="w-4 h-4 text-[#C59746]" /> +91 84690 82367
              </a>
            </div>
          </div>
          
          <div className="text-center mt-8">
            <a href="https://wa.me/919328566392" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] text-white px-6 py-3 text-xs tracking-wider uppercase font-medium hover:bg-[#128C7E] transition-colors rounded-full">
              <MessageCircle className="w-4 h-4" /> WhatsApp Us
            </a>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-12 md:py-16 bg-[#1A1A1A]">
        <div className="max-w-[600px] mx-auto px-4 text-center">
          <Crown className="w-8 h-8 text-[#C59746] mx-auto mb-4" strokeWidth={1.5} />
          <h2 className="font-heading text-2xl md:text-3xl font-light text-white mb-3">Join the JAT Family</h2>
          <p className="text-white/60 text-sm mb-6">Be the first to know about new arrivals and exclusive offers.</p>
          <div className="flex flex-col sm:flex-row gap-2 max-w-md mx-auto">
            <input type="email" placeholder="Enter your email" className="flex-1 bg-transparent border border-white/30 px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-[#C59746] text-sm" />
            <button className="bg-[#C59746] text-white px-6 py-3 text-xs tracking-wider uppercase font-medium hover:bg-[#B8863F] transition-colors">Subscribe</button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
